import { useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  WarningCircle,
  WhatsappLogo,
  Envelope,
  Check,
} from "@phosphor-icons/react";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import PhoneInput from "../../ui/PhoneInput";
import { useCreateLeadFromLandingPageMutation } from "../../../store/services/nivApi";
import MarkerStroke from "./MarkerStroke";
import useReveal from "./useReveal";
import { trackPixelEvent } from "../../../hooks/useFacebookPixel";
import "./home.css";
import "./HomeContact.css";

/*
 * Contact — the page's single conversion form. Two required fields
 * (name + mobile), optional message. Writes to niv call-requests through
 * createLeadFromLandingPage (same pipeline the sales team already works).
 * Direct channels (WhatsApp / phone / email) sit beside it for people who
 * would rather talk now.
 */

const PHONE_INTL = "972543073737";
const EMAIL = "office@clinikit.co.il";
const WHATSAPP_TEXT = encodeURIComponent("היי, אשמח לשמוע עוד על CliniKit");

const CHANNELS = [
  {
    icon: WhatsappLogo,
    label: "וואטסאפ",
    value: "שלחו הודעה, נענה מהר",
    href: `https://wa.me/${PHONE_INTL}?text=${WHATSAPP_TEXT}`,
    external: true,
  },
  { icon: Envelope, label: "אימייל", value: EMAIL, href: `mailto:${EMAIL}`, ltr: true },
];

function HomeContact({ source = "homepage_contact" }) {
  const ref = useReveal();
  const [formData, setFormData] = useState({ fullName: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [createLead, { isLoading }] = useCreateLeadFromLandingPageMutation();

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!formData.fullName.trim()) next.fullName = "יש להזין שם מלא";
    const digits = (formData.phone || "").replace(/\D/g, "");
    const isInternational = formData.phone?.startsWith("+972") && digits.length === 12;
    const isLocal = digits.length === 10 && digits.startsWith("05");
    if (!isInternational && !isLocal) next.phone = "יש להזין מספר נייד ישראלי תקין";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    try {
      await createLead({
        fullName: formData.fullName,
        phone: formData.phone,
        message: formData.message,
        source,
      }).unwrap();
      trackPixelEvent("Lead", { content_name: source });
      setSubmitted(true);
    } catch (err) {
      console.error("[CK-1053] HomeContact submit failed:", {
        errorCode: "CK-1053",
        errorMessage: err?.message,
        source,
        timestamp: new Date().toISOString(),
      });
      setErrors({ submit: "שליחת הטופס נכשלה. אפשר לנסות שוב או לכתוב לנו בוואטסאפ." });
    }
  };

  return (
    <section id="contact" className="home-section home-section--gray hcont" aria-labelledby="hcont-title">
      <div className="home-shape home-shape--blob hcont-shape-blob" aria-hidden="true" />
      <div className="home-shape home-shape--bubble hcont-shape-bubble" aria-hidden="true" />

      <div className="home-container">
        <div className="hcont-grid home-reveal" ref={ref}>
          <div className="hcont-copy">
            <h2 id="hcont-title" className="home-h2 home-h2--display">
              שיחה אחת של 15 דקות,{" "}
              <br />
              והקליניקה שלכם{" "}
              <span className="home-mark">
                מסודרת.
                <MarkerStroke />
              </span>
            </h2>
            <p className="home-sub">
              השאירו שם ונייד. נחזור אליכם תוך יום עסקים, נכיר את הקליניקה ונפתח
              לכם חשבון ניסיון — בלי כרטיס אשראי ובלי התחייבות.
            </p>

            <ul className="hcont-channels">
              {CHANNELS.map((ch) => {
                const Icon = ch.icon;
                return (
                  <li key={ch.label}>
                    <a
                      href={ch.href}
                      className="hcont-channel"
                      target={ch.external ? "_blank" : undefined}
                      rel={ch.external ? "noopener noreferrer" : undefined}
                      onClick={
                        ch.external
                          ? () => trackPixelEvent("Contact", { content_name: `${source}_whatsapp` })
                          : undefined
                      }
                    >
                      <span className="hcont-channel-icon home-bubble">
                        <Icon size={20} weight="duotone" />
                      </span>
                      <span>
                        <span className="hcont-channel-label">{ch.label}</span>
                        <span className="hcont-channel-value" dir={ch.ltr ? "ltr" : undefined}>
                          {ch.value}
                        </span>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="hcont-card home-card home-bubble--lg hcont-card--dark">
            <span className="hcont-card-glow" aria-hidden="true" />
            {submitted ? (
              <div className="hcont-success" role="status">
                <CheckCircle size={52} weight="fill" />
                <h3>תודה! קיבלנו את הפרטים.</h3>
                <p>נחזור אליכם תוך יום עסקים אחד לתיאום שיחת ההדרכה.</p>
              </div>
            ) : (
              <form className="hcont-form" onSubmit={handleSubmit} noValidate>
                <div className="hcont-field">
                  <label htmlFor="hcont-name">שם מלא</label>
                  <input
                    id="hcont-name"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    placeholder="השם שלך"
                    className={errors.fullName ? "has-error" : ""}
                    disabled={isLoading}
                    autoComplete="name"
                  />
                  {errors.fullName && <span className="hcont-error">{errors.fullName}</span>}
                </div>

                <div className="hcont-field">
                  <span className="hcont-label">טלפון נייד</span>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(val) => handleChange("phone", val)}
                    error={errors.phone}
                    disabled={isLoading}
                  />
                </div>

                <div className="hcont-field">
                  <label htmlFor="hcont-message">
                    יש משהו שכדאי שנדע? <span className="hcont-optional">(לא חובה)</span>
                  </label>
                  <textarea
                    id="hcont-message"
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="תחום הטיפול, גודל הקליניקה, שאלה..."
                    rows={3}
                    maxLength={1000}
                    disabled={isLoading}
                  />
                </div>

                {errors.submit && (
                  <div className="hcont-submit-error" role="alert">
                    <WarningCircle size={16} weight="fill" />
                    <span>{errors.submit}</span>
                  </div>
                )}

                <button type="submit" className="home-btn home-btn--primary hcont-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <LoaderLoop size="xs" />
                      שולח...
                    </>
                  ) : (
                    <>
                      קבעו לי שיחת הדרכה
                      <ArrowLeft size={18} weight="bold" />
                    </>
                  )}
                </button>

                <p className="hcont-fineprint">
                  <Check size={12} weight="bold" /> ללא ספאם · נדבר רק פעם אחת · הפרטים לא מועברים לאף גורם
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeContact;
